import React, { useState, useEffect } from 'react';
import { Project } from '../types';
import { projectService } from '../services/projectService.ts';
import { useProject } from '../context/ProjectContext.tsx';

interface ProjectSelectionProps {
    onProjectChange?: (projectId: number | null, projectName: string) => void;
}

export function ProjectSelection({ onProjectChange }: ProjectSelectionProps) {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [projectDetails, setProjectDetails] = useState({
        name: '',
        research_topic: '',
        description: '',
        category_description: '',
    });
    const { setCurrentProject } = useProject();
    const { currentProject } = useProject();

    useEffect(() => {
        const fetchAndSelect = async (id?: number) => {
            try {
                const data = await projectService.getProjects();
                setProjects(data);
                if (currentProject) {
                    id = currentProject.id;
                    const project = data.find((p) => p.id === id);
                    if (project && id) {
                        setSelectedProject(id);
                        setCurrentProject(project);
                        onProjectChange && onProjectChange(id, project.name);
                        setProjectDetails({
                            name: project.name,
                            research_topic: project.research_topic,
                            description: project.description,
                            category_description: project.category_description || '',
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };
        fetchAndSelect();
    }, []);

    const handleProjectChange = (projectId: number) => {
        setSelectedProject(projectId);
        const project = projects.find((p) => p.id === projectId);
        if (project) {
            setCurrentProject(project);
            onProjectChange && onProjectChange(projectId, project.name);
            setProjectDetails({
                name: project.name,
                research_topic: project.research_topic,
                description: project.description,
                category_description: project.category_description || '',
            });
        }
    };

    const handleCreateProject = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const createdProject = await projectService.createProject(projectDetails);
            setProjects([...projects, createdProject]);
            setShowCreateForm(false);
            // Auto-select the newly created project
            handleProjectChange(createdProject.id);
        } catch (error) {
            console.error('Error creating project:', error);
        }
    };

    const handleSave = async () => {
        if (selectedProject) {
            try {
                await projectService.updateProject(selectedProject, projectDetails);
            } catch (error) {
                console.error('Error updating project:', error);
            }
        }
    };

    return (
        <div className="project-section">
            <div className="project-selector">
                <select
                    value={selectedProject || ''}
                    onChange={(e) => handleProjectChange(Number(e.target.value))}>
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                            {project.name}
                        </option>
                    ))}
                </select>
                <button onClick={() => setShowCreateForm(!showCreateForm)}>
                    {showCreateForm ? 'Cancel' : 'Create New Project'}
                </button>
            </div>

            <form onSubmit={handleCreateProject} className="project-form">
                <input
                    type="text"
                    placeholder="Project Name"
                    value={projectDetails.name}
                    onChange={(e) => setProjectDetails({ ...projectDetails, name: e.target.value })}
                    required
                />
                <textarea
                    placeholder="Project Description"
                    value={projectDetails.description}
                    onChange={(e) => setProjectDetails({ ...projectDetails, description: e.target.value })}
                    required
                />
                <textarea
                    placeholder="Research Topic"
                    value={projectDetails.research_topic}
                    onChange={(e) => setProjectDetails({ ...projectDetails, research_topic: e.target.value })}
                    required
                />
                <textarea
                    placeholder="Define categories to extract (e.g. Study Type: RCT, Observational, etc.)"
                    value={projectDetails.category_description}
                    onChange={(e) =>
                        setProjectDetails({ ...projectDetails, category_description: e.target.value })
                    }
                    required
                />
                {showCreateForm ? (
                    <button type="submit">Create Project</button>
                ) : (
                    <button type="button" onClick={handleSave}>
                        Save Changes
                    </button>
                )}
            </form>
        </div>
    );
}
